import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function ProgramFlagsView() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [flag, setFlag] = useState(null);

    const fetchFlagDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setFlag(data.data);
            } else {
                toast.error('Failed to fetch flag details');
            }
        } catch (error) {
            console.error('Error fetching flag:', error);
            toast.error('Error fetching flag details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchFlagDetails();
    }, [id, fetchFlagDetails]);

    const getCategoryBadge = (category) => {
        const colors = {
            program: 'badge-primary',
            feature: 'badge-info',
            integration: 'badge-success',
            ui: 'badge-warning'
        };
        return colors[category] || 'badge-secondary';
    };

    const getStatusBadge = (status) => {
        const colors = {
            active: 'badge-success',
            inactive: 'badge-secondary',
            deprecated: 'badge-danger'
        };
        return colors[status] || 'badge-secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleString();
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Program Flag Details</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/programFlags">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : flag ? (
                        <div className="row">
                            <div className="col-lg-8">
                                {/* Basic Information */}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Basic Information</h5>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: '200px' }}>Key</th>
                                                    <td><code className="text-primary">{flag.key}</code></td>
                                                </tr>
                                                <tr>
                                                    <th>Name</th>
                                                    <td>{flag.name}</td>
                                                </tr>
                                                <tr>
                                                    <th>Description</th>
                                                    <td>{flag.description || <span className="text-muted">No description</span>}</td>
                                                </tr>
                                                <tr>
                                                    <th>Category</th>
                                                    <td>
                                                        <span className={`badge ${getCategoryBadge(flag.category)}`}>
                                                            {flag.category}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Phase</th>
                                                    <td>
                                                        <span className="badge badge-outline-primary">
                                                            Phase {flag.phase}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Display Order</th>
                                                    <td>{flag.displayOrder}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Configuration */}
                                {flag.config && Object.keys(flag.config).length > 0 && (
                                    <div className="card">
                                        <div className="card-header">
                                            <h5 className="card-title mb-0">Configuration</h5>
                                        </div>
                                        <div className="card-body">
                                            <pre className="bg-light p-3 rounded" style={{ fontSize: '0.875rem' }}>
                                                {JSON.stringify(flag.config, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {/* Plan Restrictions */}
                                {flag.planRestrictions?.enabled && (
                                    <div className="card">
                                        <div className="card-header">
                                            <h5 className="card-title mb-0">Plan Restrictions</h5>
                                        </div>
                                        <div className="card-body">
                                            <p className="mb-2"><strong>Allowed Plans:</strong></p>
                                            <div>
                                                {flag.planRestrictions.allowedPlans?.map(plan => (
                                                    <span key={plan} className="badge badge-info mr-2">
                                                        {plan}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rollout */}
                                {flag.rollout?.enabled && (
                                    <div className="card">
                                        <div className="card-header">
                                            <h5 className="card-title mb-0">Rollout Configuration</h5>
                                        </div>
                                        <div className="card-body">
                                            <table className="table table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '200px' }}>Strategy</th>
                                                        <td>{flag.rollout.strategy}</td>
                                                    </tr>
                                                    {flag.rollout.strategy === 'percentage' && (
                                                        <tr>
                                                            <th>Percentage</th>
                                                            <td>
                                                                <div className="progress" style={{ width: '200px' }}>
                                                                    <div
                                                                        className="progress-bar bg-primary"
                                                                        style={{ width: `${flag.rollout.percentage}%` }}
                                                                    >
                                                                        {flag.rollout.percentage}%
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="col-lg-4">
                                {/* Status Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Status</h5>
                                    </div>
                                    <div className="card-body text-center">
                                        <div className="mb-3">
                                            <span className={`badge ${getStatusBadge(flag.status)} px-3 py-2`} style={{ fontSize: '1rem' }}>
                                                {flag.status.toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className={`badge ${flag.isGloballyEnabled ? 'badge-success' : 'badge-secondary'} px-3 py-2`} style={{ fontSize: '1rem' }}>
                                                {flag.isGloballyEnabled ? 'ENABLED' : 'DISABLED'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Schedule */}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Schedule</h5>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th>Start</th>
                                                    <td>{formatDate(flag.schedule?.startDate)}</td>
                                                </tr>
                                                <tr>
                                                    <th>End</th>
                                                    <td>{formatDate(flag.schedule?.endDate)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Metadata */}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Metadata</h5>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th>Version</th>
                                                    <td>{flag.metadata?.version || '1.0'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Last Updated</th>
                                                    <td>{formatDate(flag.metadata?.lastUpdated)}</td>
                                                </tr>
                                                {flag.metadata?.notes && (
                                                    <tr>
                                                        <th>Notes</th>
                                                        <td>{flag.metadata.notes}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Audit */}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Audit Trail</h5>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th>Created</th>
                                                    <td>{formatDate(flag.createdAt)}</td>
                                                </tr>
                                                <tr>
                                                    <th>Created By</th>
                                                    <td>{flag.createdBy && typeof flag.createdBy === 'object' ? (flag.createdBy.name || flag.createdBy.email || 'System') : (flag.createdBy || 'System')}</td>
                                                </tr>
                                                <tr>
                                                    <th>Updated</th>
                                                    <td>{formatDate(flag.updatedAt)}</td>
                                                </tr>
                                                <tr>
                                                    <th>Updated By</th>
                                                    <td>{flag.updatedBy && typeof flag.updatedBy === 'object' ? (flag.updatedBy.name || flag.updatedBy.email || 'System') : (flag.updatedBy || 'System')}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="card">
                                    <div className="card-body">
                                        <Link to={`/programFlagsAdd/${flag._id}`} className="btn btn-primary btn-block">
                                            <i className="fa-solid fa-pen mr-2"></i>
                                            Edit Flag
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-5">
                            <p>Flag not found</p>
                            <Link to="/programFlags" className="btn btn-primary">
                                Back to List
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ProgramFlagsView
